import { Dialog, Transition } from "@headlessui/react";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Fragment, useRef, useState } from "react";
import { DateRangePicker, createStaticRanges, defaultStaticRanges } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

interface IOMCalendarComponentProps {
  selectionDate: any;
  setSelectionDate: any;
  startDate: any;
  label?: string;
  endDate: any;
  handelClick: any;
  containerClassName?: string;
  isFutureDateDisabled?: boolean;
  isPastDateDisabled?: boolean;
  isPaddingOnTop?: boolean;
  isDynamicCalender?: boolean;
  isShowSelectAll?: boolean;
}

const getPastDate = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

export const ALL_TIME_SENTINEL = new Date(0);

const baseExtendedStaticRanges = [
  ...defaultStaticRanges.filter((r) => r.label !== "Yesterday"),
  ...createStaticRanges([
    {
      label: "Last 30 Days",
      range: () => ({ startDate: getPastDate(30), endDate: new Date() }),
    },
    {
      label: "Last 90 Days",
      range: () => ({ startDate: getPastDate(90), endDate: new Date() }),
    },
    {
      label: "Last 120 Days",
      range: () => ({ startDate: getPastDate(120), endDate: new Date() }),
    },
    {
      label: "Last 365 Days",
      range: () => ({ startDate: getPastDate(365), endDate: new Date() }),
    },
  ]),
];

const selectAllStaticRange = createStaticRanges([
  {
    label: "Select all",
    range: () => ({ startDate: ALL_TIME_SENTINEL, endDate: ALL_TIME_SENTINEL }),
  },
]);

export const isAllTimeRange = (date: Date | null | undefined) =>
  !!date && new Date(date).getTime() === ALL_TIME_SENTINEL.getTime();

const OMCalendarComponent: React.FC<IOMCalendarComponentProps> = ({
  selectionDate,
  setSelectionDate,
  startDate,
  label,
  endDate,
  handelClick,
  containerClassName,
  isFutureDateDisabled,
  isPastDateDisabled,
  isPaddingOnTop,
  isDynamicCalender,
  isShowSelectAll,
}) => {
  const staticRanges = isShowSelectAll
    ? [...baseExtendedStaticRanges, ...selectAllStaticRange]
    : baseExtendedStaticRanges;
  const [open, setOpen] = useState(false);
  const cancelButtonRef = useRef(null);

  const handleOpen = (e) => {
    e.preventDefault(); // Prevent default browser behavior
    e.stopPropagation(); // Stop the event from propagating to parent elements

    setOpen(true); // Perform your action
  };

  const handleSelect = (ranges: any) => {
    setSelectionDate(ranges.selectionDate);
  };

  return (
    <div className={clsx("relative", containerClassName)}>
      {label && <span className="-top-4 left-1 absolute text-gray-600 text-xs">{label}</span>}
      <button
        type="button"
        className={`flex items-center bg-white whitespace-nowrap w-full gap-2 border-gray-300 bg-primary-color px-3 ${isPaddingOnTop ? "py-0" : "py-1"} border border-solid rounded-lg w-full text-black text-xs cursor-pointer`}
        onClick={handleOpen}
      >
        <span>
          <CalendarDaysIcon className="w-4 h-4" />
        </span>
        <span className={`${isDynamicCalender ? "py-[2px] font-medium text-xs" : "py-[6px] text-xs font-medium rounded-lg pl-2"}`}>
          {startDate} to {endDate}
        </span>
        <span>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M7.25836 9.75837L9.41669 11.9167C9.74169 12.2417 10.2667 12.2417 10.5917 11.9167L12.75 9.75837C13.275 9.23337 12.9 8.33337 12.1584 8.33337H7.84169C7.10002 8.33337 6.73336 9.23337 7.25836 9.75837Z"
              fill="#24272C"
            />
          </svg>
        </span>
      </button>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="z-50 fixed inset-0 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => setOpen(false)}
        >
          <div className="flex justify-center items-center min-h-screen">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="z-40 bg-white p-4 rounded-md max-w-[1200px]">
                <h3 className="date-filter-by-date">Select Date</h3>
                <div>
                  <DateRangePicker
                    ranges={[selectionDate]}
                    onChange={handleSelect}
                    moveRangeOnFirstSelection={false}
                    maxDate={isFutureDateDisabled ? new Date() : undefined}
                    minDate={isPastDateDisabled ? new Date() : undefined}
                    staticRanges={staticRanges}
                    inputRanges={[]}
                  />
                </div>
                <div className="flex justify-end mt-3 mr-5">
                  <button
                    onClick={() => {
                      setOpen(false);
                      handelClick();
                    }}
                    className="bg-black px-3 py-2 border-none rounded-md text-white text-xs cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default OMCalendarComponent;
